import { Injectable } from '@angular/core';
import type { Workbook, Worksheet } from 'exceljs';
import { CustomerProjectType } from '../../../customer-project-type.enum';
import { CustomerProject } from '../../../customer-project.interface';
import { Workarea } from '../../../workarea.class';
import {
    ASPHALT_LAYERS,
    ASPHALT_TYPES,
    DATE_KEYS,
    GENERAL_TYPES,
    WORKAREA_EXCEL_COLUMNS,
    WorkareaExcelColumn,
} from './export-excel-builder.constants';
import {
    asphaltTypeColor,
    byAsphaltTypeOrder,
    columnLetter,
    fillCell,
    fitWidth,
    isTruthy,
    styleHeader,
    toCellValue,
    toExcelDate,
} from './export-excel-builder.utils';

@Injectable({
    providedIn: 'root',
})
export class ExportExcelBuilderService {
    async build(workareas: Workarea[], project: CustomerProject, restricted = false) {
        const workbook = new (await this.loadExcelJs()).Workbook();
        // ExcelJS stores no formula results; let Excel recalculate on open
        workbook.calcProperties.fullCalcOnLoad = true;
        const type = project.type;
        const columns = WORKAREA_EXCEL_COLUMNS[type];
        const sheet = workbook.addWorksheet('Worksheet');
        sheet.addRow(columns.map(column => column.header));
        styleHeader(sheet.getRow(1));
        sheet.views = [{ state: 'frozen', ySplit: 1 }];
        const letter = (key: string) => columnLetter(sheet, columns, key);

        this.addWorkareaRows(sheet, columns, workareas, project, type, letter, restricted);
        this.formatColumns(sheet, columns);
        if (ASPHALT_TYPES.includes(type)) {
            this.addAsphaltTotals(workbook, sheet, letter);
        }

        return workbook.xlsx.writeBuffer();
    }

    // Lazy: ExcelJS (~1.7 MB) stays out of the main bundle
    private async loadExcelJs(): Promise<typeof import('exceljs')> {
        const mod: any = await import('exceljs');
        return mod.default ?? mod;
    }

    private addWorkareaRows(
        sheet: Worksheet,
        columns: WorkareaExcelColumn[],
        workareas: Workarea[],
        project: CustomerProject,
        type: CustomerProjectType,
        letter: (key: string) => string,
        restricted: boolean
    ) {
        const isAsphalt = ASPHALT_TYPES.includes(type);
        const isGeneral = GENERAL_TYPES.includes(type);

        workareas.forEach(workarea => {
            const extra = { ...workarea.extra, ...project.extra };
            // One row per area; a workarea without areas still gets one row
            const areas = workarea.areas?.length ? workarea.areas : [null];
            areas.forEach(area => {
                const rowNumber = sheet.rowCount + 1;
                const size = (key: 'width' | 'length') => parseFloat(String(area?.[key] ?? '').replace(/,/g, '.')) || 0;
                sheet.addRow(
                    columns.map(({ key }) => {
                        if (key === 'maps_url') {
                            const url = `https://www.google.com/maps?q=${workarea.lat},${workarea.lng}`;
                            return { text: url, hyperlink: url };
                        }
                        if (key === 'width' || key === 'length') {
                            if (!area) {
                                return isGeneral ? 'n.v.t.' : null;
                            }
                            if (area[key] == null) {
                                return restricted ? null : 0;
                            }
                            return size(key);
                        }
                        if (key === 'surface') {
                            // Cached result for viewers that don't calculate (Protected View, previews)
                            return {
                                formula: `N(${letter('width')}${rowNumber})*N(${letter('length')}${rowNumber})`,
                                result: size('width') * size('length'),
                            };
                        }
                        if (DATE_KEYS.includes(key)) {
                            return toExcelDate(workarea[key as keyof Workarea]);
                        }
                        if (key.startsWith('extra.')) {
                            const field = key.slice('extra.'.length);
                            if (field === 'klinkerfloor' && isAsphalt && !restricted) {
                                return isTruthy(extra[field]) ? 'Ja' : 'Nee';
                            }
                            if ((field === 'wearlayer' || field === 'execution') && isGeneral && !restricted) {
                                return extra[field] || 'Onbekend';
                            }
                            return toCellValue(extra[field]);
                        }
                        return toCellValue(workarea[key as keyof Workarea]);
                    })
                );
            });
        });
    }

    private formatColumns(sheet: Worksheet, columns: WorkareaExcelColumn[]) {
        columns.forEach(({ key }, index) => {
            const column = sheet.getColumn(index + 1);
            // Show date and time, like the old export (d/m/yy h:mm)
            if (DATE_KEYS.includes(key)) {
                column.numFmt = 'd/m/yy h:mm';
            }
            if (key === 'maps_url') {
                column.eachCell((cell, rowNumber) => {
                    if (rowNumber > 1) {
                        cell.font = { color: { argb: 'FF0000FF' }, underline: true };
                    }
                });
            }
            fitWidth(column);
        });
    }

    private addAsphaltTotals(workbook: Workbook, sheet: Worksheet, letter: (key: string) => string) {
        // Absolute range of a main-sheet column, e.g. 'Worksheet'!$O$2:$O$4
        const range = (key: string) => `'${sheet.name}'!$${letter(key)}$2:$${letter(key)}$${sheet.rowCount}`;
        // Asphalt types per layer, as found on the main sheet
        const layers = Object.entries(ASPHALT_LAYERS).map(([key, label]) => {
            const asphaltTypes = new Set<string>();
            sheet.getColumn(letter(`extra.${key}`)).eachCell((cell, rowNumber) => {
                const asphaltType = String(cell.value ?? '').trim();
                if (rowNumber > 1 && asphaltType && asphaltType.toLowerCase() !== 'niet van toepassing') {
                    asphaltTypes.add(asphaltType);
                }
            });
            return { key, label, asphaltTypes: [...asphaltTypes].sort(byAsphaltTypeOrder) };
        });
        const allAsphaltTypes = [...new Set([...layers.flatMap(layer => layer.asphaltTypes), 'Frezen'])].sort(
            byAsphaltTypeOrder
        );

        // The density table sits below table 1: type rows + totals + empty + milling + empty
        const asphaltTypeRowCount = Math.max(
            layers.reduce((count, layer) => count + layer.asphaltTypes.length, 0),
            1
        );
        const densityHeaderRow = 2 + asphaltTypeRowCount + 4;
        const densityRange = `$A$${densityHeaderRow + 1}:$B$${densityHeaderRow + allAsphaltTypes.length}`;
        // Thickness × surface × density / 1000
        const tons = (row: number) => `IFERROR(C${row}*D${row}*VLOOKUP(B${row},${densityRange},2,0)/1000,0)`;

        const totalsSheet = workbook.addWorksheet('Asfalttotalen', { views: [{ state: 'frozen', ySplit: 1 }] });
        styleHeader(
            totalsSheet.addRow([
                'Laag',
                'Asfaltsoort',
                'Dikte laag (mm)',
                'Oppervlakte (m²)',
                'Tonnen rood',
                'Tonnen zwart',
            ])
        );

        layers.forEach(({ key, label, asphaltTypes }) =>
            asphaltTypes.forEach(asphaltType => {
                const row = totalsSheet.rowCount + 1;
                totalsSheet.addRow([
                    label,
                    asphaltType,
                    40,
                    // Surface of all main-sheet rows where this layer has this asphalt type
                    { formula: `SUMIF(${range(`extra.${key}`)},B${row},${range('surface')})` },
                    { formula: `IF(ISNUMBER(SEARCH("rood",B${row})),${tons(row)},0)` },
                    { formula: `IF(ISNUMBER(SEARCH("rood",B${row})),0,${tons(row)})` },
                ]);
                fillCell(totalsSheet.getCell(`B${row}`), asphaltTypeColor(asphaltType));
            })
        );
        if (totalsSheet.rowCount === 1) {
            totalsSheet.addRow(['Geen data', 'Geen asfaltlagen gevonden in selectie']);
        }

        const totalsRow = totalsSheet.rowCount + 1;
        totalsSheet.addRow([
            'Totaal',
            null,
            null,
            ...['D', 'E', 'F'].map(col => ({ formula: `SUM(${col}2:${col}${totalsRow - 1})` })),
        ]).font = { bold: true };

        // Milling (Frezen): surface of all main-sheet rows without klinkers
        const millingRow = totalsRow + 2;
        totalsSheet.insertRow(millingRow, [
            'Frezen',
            'Frezen',
            40,
            { formula: `SUMIF(${range('extra.klinkerfloor')},"Nee",${range('surface')})` },
            0,
            { formula: tons(millingRow) },
        ]).font = { bold: true };
        fillCell(totalsSheet.getCell(`B${millingRow}`), asphaltTypeColor('Frezen'));
        for (let row = 2; row <= millingRow; row++) {
            ['D', 'E', 'F'].forEach(col => (totalsSheet.getCell(`${col}${row}`).numFmt = '#,##0.00'));
        }

        // Density table (soortelijk gewicht), editable in Excel
        const densityHeader = totalsSheet.getRow(densityHeaderRow);
        densityHeader.values = ['Asfaltsoort', 'Soortelijk gewicht (kg/m² per mm)'];
        styleHeader(densityHeader);
        allAsphaltTypes.forEach((asphaltType, index) => {
            const row = totalsSheet.getRow(densityHeaderRow + 1 + index);
            row.values = [asphaltType, 2.5];
            fillCell(row.getCell(1), asphaltTypeColor(asphaltType));
            row.getCell(2).numFmt = '#,##0.00';
        });
        ['A', 'B', 'C', 'D', 'E', 'F'].forEach(col => fitWidth(totalsSheet.getColumn(col)));
    }
}
